/* import __COLOCATED_TEMPLATE__ from './photodiode.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { numberToUserDisplay, userEntryToNumber } from '@ebiz/pretty-num';
import ModelService from '../../services/model';
import SensorService from '../../services/sensor';
import { SensorData } from 'photodiode/models/sensor';
import type IntlService from 'ember-intl/services/intl';
import { BufferedChangeset } from 'ember-changeset/types';

class SensorDialogsState {
  @tracked sensorDialogEnabled = false;
  @tracked currentCalcDialogEnabled = false;
  @tracked currentCalcDialogDelivered = false;
  @tracked currentCalculationAccepted = false;

  enableSensorSelection() {
    this.sensorDialogEnabled = true;
  }
  cancelSensorSelection() {
    this.sensorDialogEnabled = false;
  }
  selectSensor() {
    this.sensorDialogEnabled = false;
    this.currentCalculationAccepted = false;
    this.currentCalcDialogEnabled = true;
  }
  closeCurrentCalculation() {
    this.currentCalcDialogEnabled = false;
    if (!this.currentCalculationAccepted) {
      this.sensorDialogEnabled = true;
    }
  }
  acceptCurrentCalculation() {
    this.currentCalculationAccepted = true;
  }
}
interface PhotodiodeArgs {
  changeset: BufferedChangeset;
  updateEnabledTabs?: (tab: string, isInvalid: boolean) => void;
  systemDialogFunc?: (status: string, message: string, msgparams?: Record<string, unknown>) => void;
  openNotification?: (title: string, message: string, msgparams?: Record<string, unknown>) => void;
  isCriticalError: boolean;
}

export default class Photodiode extends Component<PhotodiodeArgs> {
  @tracked selectedPart?: string;
  @service declare intl: IntlService;
  @service('model') declare modelService: ModelService;
  @service('sensor') declare sensorService: SensorService;
  @tracked capacitanceWarningEnabled = false;
  @tracked sensorDialogState = new SensorDialogsState();

  get changeset(): BufferedChangeset {
    return this.args.changeset;
  }

  get isPositive(): boolean {
    return this.changeset.get('Bias') === '+' ? true : false;
  }

  get isNegative(): boolean {
    return this.changeset.get('Bias') === '-' ? true : false;
  }

  get invalidVr(): boolean {
    return this.checkValidity('Vr');
  }

  get invalidCd(): boolean {
    return this.checkValidity('Cd');
  }

  get invalidRsh(): boolean {
    return this.checkValidity('Rsh');
  }

  get invalidIp(): boolean {
    return this.checkValidity('Ip');
  }

  get selectedPartData(): SensorData | undefined {
    return this.modelService.sensors?.find((rowData) => {
      return rowData.partNumber === this.selectedPart;
    });
  }

  checkValidity(key: string): boolean {
    let result = false;
    this.changeset.get('errors').forEach((item: { key: string }) => {
      if (item.key === key) {
        result = true;
      }
    });
    return result;
  }

  validateTextField(name: string, value: string): void {
    let candidate = userEntryToNumber(value, NaN);
    if (isNaN(candidate)) {
      this.changeset.addError(name, {
        value: value,
        validation: this.intl.t('validation-error.invalid-input-error', { value: value, field: name }),
      });
    } else {
      this.changeset.set(name, candidate);
    }
  }

  canonicalize(value: string): string {
    return numberToUserDisplay(userEntryToNumber(value, NaN), 'Engineering', 3);
  }

  @action updateVr(event: InputEvent): void {
    let target = event.target as HTMLInputElement;
    this.validateTextField('Vr', target.value);
    if (!this.changeset.isInvalid) {
      this.changeset.save();
      target.value = this.canonicalize(target.value);
    }
    if (this.args.updateEnabledTabs)
      this.args.updateEnabledTabs('photodiode', this.changeset.isInvalid || this.args.isCriticalError);
  }

  @action updateBias(event: InputEvent): void {
    let target = event.target as HTMLInputElement;
    this.changeset.set('Bias', target.value);
    if (!this.changeset.isInvalid) {
      this.changeset.save();
    }
    if (this.args.updateEnabledTabs)
      this.args.updateEnabledTabs('photodiode', this.changeset.isInvalid || this.args.isCriticalError);
  }

  @action updateCd(event: InputEvent): void {
    let target = event.target as HTMLInputElement;
    this.validateTextField('Cd', target.value);
    if (!this.changeset.isInvalid) {
      this.selectedPart = '';
      this.changeset.set('Photodiode', '');
      this.changeset.save();
      target.value = this.canonicalize(target.value);
    }
    if (this.args.updateEnabledTabs)
      this.args.updateEnabledTabs('photodiode', this.changeset.isInvalid || this.args.isCriticalError);
  }

  @action updateRsh(event: InputEvent): void {
    let target = event.target as HTMLInputElement;
    this.validateTextField('Rsh', target.value);
    if (!this.changeset.isInvalid) {
      this.selectedPart = '';
      this.changeset.set('Photodiode', '');
      this.changeset.save();
      target.value = this.canonicalize(target.value);
    }
    if (this.args.updateEnabledTabs)
      this.args.updateEnabledTabs('photodiode', this.changeset.isInvalid || this.args.isCriticalError);
  }

  @action updateIp(event: InputEvent): void {
    let target = event.target as HTMLInputElement;
    this.validateTextField('Ip', target.value);
    if (!this.changeset.isInvalid) {
      this.selectedPart = '';
      this.changeset.set('Photodiode', '');
      this.changeset.save();
      target.value = this.canonicalize(target.value);
    }
    if (this.args.updateEnabledTabs)
      this.args.updateEnabledTabs('photodiode', this.changeset.isInvalid || this.args.isCriticalError);
  }

  @action assignPeakCurrent(peakCurrent: string): void {
    this.sensorDialogState.acceptCurrentCalculation();
    // Need to keep text and numbers in sync
    let sensorData = this.selectedPartData;
    if (sensorData) {
      // Rollback Properties that weren't saved for Cd, Rsh and Ip
      // Fortunately, these are the only ones that caused an error as valid values are already saved
      this.changeset.rollbackProperty('Cd');
      this.changeset.rollbackProperty('Rsh');
      this.changeset.rollbackProperty('Ip');
      this.changeset.execute();
      // Update State Directly as this data is valid
      this.modelService.state.Cd = userEntryToNumber(sensorData.capacitance as string, NaN);
      this.modelService.state.Rsh = userEntryToNumber(sensorData.resistance as string, NaN);
      this.modelService.state.Ip = userEntryToNumber(peakCurrent, NaN);
      this.modelService.state.Photodiode = this.selectedPart as string;
    }
    if (this.args.updateEnabledTabs)
      this.args.updateEnabledTabs('photodiode', this.changeset.isInvalid || this.args.isCriticalError);
  }

  @action showCapacitanceWarning(): void {
    this.capacitanceWarningEnabled = true;
  }

  @action hideCapacitanceWarning(): void {
    this.capacitanceWarningEnabled = false;
  }

  @action openSensorDialog(): void {
    if (this.sensorService.sensors) {
      if (this.args.systemDialogFunc) this.args.systemDialogFunc('ON', 'application-busy');
      this.sensorService.sensors.then(async (sensors) => {
        if (this.args.systemDialogFunc) this.args.systemDialogFunc('OFF', '');
        if (sensors !== null) {
          this.sensorDialogState.enableSensorSelection();
        } else {
          if (this.args.openNotification)
            this.args.openNotification('dialogs.system.title', 'dialogs.system.error-sensor-data');
        }
      });
    }
  }

  @action closeSensorDialog(): void {
    this.sensorDialogState.cancelSensorSelection();
  }

  @action selectSensor(selectedPart: string): void {
    this.selectedPart = selectedPart;
    this.sensorDialogState.selectSensor();
  }

  @action closeCurrentCalcDialog(): void {
    this.sensorDialogState.closeCurrentCalculation();
  }
}
